import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import {
  CardGiftcard,
  CheckCircle,
  Schedule,
  Cancel,
  Redeem,
} from '@mui/icons-material'
import { subscriptionsApi } from '@api/endpoints/subscriptions'
import type { SubscriptionCreditDto } from '@api/types/subscription'
import { format } from 'date-fns'

export function CreditsSection() {
  const [credits, setCredits] = useState<SubscriptionCreditDto[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [redeemingId, setRedeemingId] = useState<string | null>(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [selectedCredit, setSelectedCredit] = useState<SubscriptionCreditDto | null>(null)
  const [redeemSuccess, setRedeemSuccess] = useState<string | null>(null)

  useEffect(() => {
    loadCredits()
  }, [])

  const loadCredits = async () => {
    try {
      setIsLoading(true)
      setError(null)
      const data = await subscriptionsApi.getMyCredits()
      setCredits(data.credits ?? [])
    } catch (err) {
      setError('Failed to load credits')
      console.error('Error loading credits:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const handleRedeemClick = (credit: SubscriptionCreditDto) => {
    setSelectedCredit(credit)
    setConfirmDialogOpen(true)
  }

  const handleConfirmRedeem = async () => {
    if (!selectedCredit) return

    try {
      setRedeemingId(selectedCredit.id)
      setConfirmDialogOpen(false)
      await subscriptionsApi.redeemCredit(selectedCredit.id)
      setRedeemSuccess(
        `Successfully redeemed ${selectedCredit.creditMonths} month${selectedCredit.creditMonths > 1 ? 's' : ''} of ${selectedCredit.plan.displayName}!`
      )
      // Reload credits to reflect the change
      await loadCredits()
    } catch (err) {
      setError('Failed to redeem credit. Please try again.')
      console.error('Error redeeming credit:', err)
    } finally {
      setRedeemingId(null)
      setSelectedCredit(null)
    }
  }

  const getStatusColor = (statusDisplayName: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (statusDisplayName) {
      case 'Pending':
        return 'info'
      case 'Redeemed':
        return 'success'
      case 'Expired':
        return 'warning'
      case 'Revoked':
        return 'error'
      default:
        return 'default'
    }
  }

  const getStatusIcon = (statusDisplayName: string) => {
    switch (statusDisplayName) {
      case 'Pending':
        return <Schedule fontSize="small" />
      case 'Redeemed':
        return <CheckCircle fontSize="small" />
      case 'Expired':
      case 'Revoked':
        return <Cancel fontSize="small" />
      default:
        return null
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>
  }

  // Separate redeemable credits from others using canRedeem field
  const pendingCredits = credits.filter((c) => c.canRedeem)
  const otherCredits = credits.filter((c) => !c.canRedeem)

  if (credits.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          p: 6,
          textAlign: 'center',
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <CardGiftcard sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Credits Available
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You don't have any subscription credits yet. Credits may be granted through promotions,
          referrals, or special offers.
        </Typography>
      </Paper>
    )
  }

  return (
    <Box>
      {redeemSuccess && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setRedeemSuccess(null)}>
          {redeemSuccess}
        </Alert>
      )}

      {/* Pending Credits (Available to Redeem) */}
      {pendingCredits.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Available Credits
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
              gap: 2,
            }}
          >
            {pendingCredits.map((credit) => (
              <Card
                key={credit.id}
                elevation={2}
                sx={{
                  border: '2px solid',
                  borderColor: 'primary.main',
                  position: 'relative',
                  overflow: 'visible',
                }}
              >
                <Chip
                  icon={<CardGiftcard fontSize="small" />}
                  label="Available"
                  color="primary"
                  size="small"
                  sx={{
                    position: 'absolute',
                    top: -12,
                    left: 16,
                  }}
                />
                <CardContent sx={{ pt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {credit.plan.displayName}
                  </Typography>
                  <Typography variant="h4" color="primary" sx={{ fontWeight: 700, my: 1 }}>
                    {credit.creditMonths} Month{credit.creditMonths > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {credit.reason}
                  </Typography>
                  {credit.daysUntilExpiration !== null && credit.daysUntilExpiration > 0 && (
                    <Typography variant="caption" color="warning.main">
                      Expires in {credit.daysUntilExpiration} day{credit.daysUntilExpiration !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </CardContent>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    startIcon={<Redeem />}
                    onClick={() => handleRedeemClick(credit)}
                    disabled={redeemingId === credit.id}
                  >
                    {redeemingId === credit.id ? 'Redeeming...' : 'Redeem Now'}
                  </Button>
                </CardActions>
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Credit History */}
      {otherCredits.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Credit History
          </Typography>
          <Stack spacing={2}>
            {otherCredits.map((credit) => (
              <Paper
                key={credit.id}
                elevation={0}
                sx={{
                  p: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                    {credit.plan.displayName} - {credit.creditMonths} Month
                    {credit.creditMonths > 1 ? 's' : ''}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {credit.reason}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Granted {format(new Date(credit.grantedAt), 'MMM d, yyyy')}
                    {credit.redeemedAt && (
                      <> • Redeemed {format(new Date(credit.redeemedAt), 'MMM d, yyyy')}</>
                    )}
                  </Typography>
                </Box>
                <Chip
                  icon={getStatusIcon(credit.statusDisplayName)}
                  label={credit.statusDisplayName}
                  color={getStatusColor(credit.statusDisplayName)}
                  size="small"
                />
              </Paper>
            ))}
          </Stack>
        </Box>
      )}

      {/* Confirm Redeem Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>Redeem Credit</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to redeem this credit?
            {selectedCredit && (
              <>
                <br />
                <br />
                <strong>{selectedCredit.plan.displayName}</strong> -{' '}
                {selectedCredit.creditMonths} month{selectedCredit.creditMonths > 1 ? 's' : ''}
                <br />
                <br />
                This will be applied to your subscription immediately:
                <ul>
                  <li>If you have no subscription, a new one will be created</li>
                  <li>If you're on the free tier, you'll be upgraded</li>
                  <li>If you're on the same plan, your subscription will be extended</li>
                  <li>If you're on a different plan, you'll be switched to this plan</li>
                </ul>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmRedeem} variant="contained" color="primary">
            Redeem Credit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
